import { ImgHTMLAttributes, SyntheticEvent, useEffect, useRef, useState } from "react"
import type { ReactNode } from "react"
import DotsLoader from "components/DotsLoader"

interface Props extends Omit<ImgHTMLAttributes<HTMLImageElement>, "alt" | "src"> {
  alt: string
  src: string
  fallbackSrcs?: string[]
  fallback?: ReactNode
  wrapperClassName?: string
  loaderLabel?: string
  hideLoaderLabel?: boolean
}

const LoadableImage = ({
  alt,
  src,
  fallbackSrcs = [],
  fallback,
  wrapperClassName = "",
  loaderLabel = "Loading image",
  hideLoaderLabel = false,
  className = "",
  onLoad,
  onError,
  ...imageProps
}: Props) => {
  const imageRef = useRef<HTMLImageElement>(null)
  const sourceList = [src, ...fallbackSrcs].filter((source, index, sources): source is string =>
    Boolean(source) && sources.indexOf(source) === index
  )
  const sourceKey = sourceList.join("\0")
  const [activeSourceIndex, setActiveSourceIndex] = useState(0)
  const [loaded, setLoaded] = useState(false)
  const [failed, setFailed] = useState(false)
  const currentSrc = sourceList[Math.min(activeSourceIndex, Math.max(sourceList.length - 1, 0))] || src

  useEffect(() => {
    setActiveSourceIndex(0)
    setLoaded(false)
    setFailed(false)
  }, [sourceKey])

  useEffect(() => {
    const image = imageRef.current
    if (!image?.complete) return

    if (image.naturalWidth > 0) {
      setLoaded(true)
    } else if (activeSourceIndex < sourceList.length - 1) {
      setLoaded(false)
      setActiveSourceIndex((index) => Math.min(index + 1, sourceList.length - 1))
    } else {
      setFailed(true)
    }
  }, [activeSourceIndex, currentSrc, sourceList.length])

  const handleLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    setLoaded(true)
    onLoad?.(event)
  }

  const handleError = (event: SyntheticEvent<HTMLImageElement>) => {
    setLoaded(false)
    if (activeSourceIndex < sourceList.length - 1) {
      setActiveSourceIndex((index) => Math.min(index + 1, sourceList.length - 1))
      return
    }

    setFailed(true)
    onError?.(event)
  }

  return (
    <div
      className={`relative overflow-hidden bg-gallery-surface ${wrapperClassName}`}
      aria-busy={!loaded && !failed}
    >
      {!loaded && !failed && (
        <div className="gallery-image-loader">
          <DotsLoader
            label={loaderLabel}
            hideLabel={hideLoaderLabel}
            compact={hideLoaderLabel}
            shuffleKey={src}
          />
        </div>
      )}
      {failed ? (
        fallback ?? (
          <div className="flex h-full min-h-[120px] w-full items-center justify-center px-4 text-center">
            <span className="gallery-label text-gallery-muted">Preview not loading</span>
          </div>
        )
      ) : (
        <img
          {...imageProps}
          ref={imageRef}
          src={currentSrc}
          alt={alt}
          onLoad={handleLoad}
          onError={handleError}
          className={`${className} transition-opacity duration-500 ${loaded ? "opacity-100" : "opacity-0"}`}
        />
      )}
    </div>
  )
}

export default LoadableImage
